/**
 * disposal_report_templates — Phase 16 / Task 16.5 (DEC-162).
 *
 * Per-facility (or company-wide) custom report template config backing the
 * `module_custom_reports` toggle (DEC-005 / DEC-158). DISPOSAL_ADMIN
 * defines a template (fields, grouping, date_format, volume_unit,
 * custom_header) then triggers async report generation over a date range.
 *
 * facility_id semantics:
 *   NULL          → company-wide template (report covers ALL of the
 *                   disposal_company's facilities in one output)
 *   <specific>    → single-facility template
 *
 * App-layer FK guard: Postgres does NOT allow CHECK subqueries; the
 * create-template route SELECTs before INSERT to confirm the facility
 * belongs to the caller's disposal_company. Cross-company reference →
 * 400 DISPOSAL_REPORT_TEMPLATE_FACILITY_MISMATCH.
 *
 * Format allowlist: CSV / EXCEL / PDF. PDF is accepted in the schema for
 * forward-compat with Phase 17 / 19 shared Puppeteer infra — the generate
 * endpoint returns 501 DISPOSAL_REPORT_PDF_NOT_IMPLEMENTED and the sweeper
 * marks the run FAILED with reason 'PDF_NOT_IMPLEMENTED' immediately if a
 * PDF template is somehow queued.
 *
 * Soft-delete via archived_at + archived_by_user_id. The list endpoint
 * excludes archived rows by default; hard-delete is blocked while any
 * disposal_report_runs row references the template (ON DELETE RESTRICT
 * FK) so the audit trail stays intact.
 *
 * RLS: tenant-isolated via subquery to disposal_companies.tenant_id.
 * Same pattern as disposal_daily_email_recipients (16.4).
 */

import { check, index, jsonb, pgPolicy, pgTable, text, timestamp, uuid } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { appUserRole } from './enums.js';
import { disposalCompanies, disposalFacilities } from './disposals.js';
import { users } from './users.js';

export const disposalReportTemplates = pgTable(
  'disposal_report_templates',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    disposalCompanyId: uuid('disposal_company_id')
      .notNull()
      .references(() => disposalCompanies.id, { onDelete: 'cascade' }),
    // NULL = company-wide template; non-NULL = site-scoped. App-layer route
    // guards cross-company references (no CHECK subquery in PG).
    facilityId: uuid('facility_id').references(() => disposalFacilities.id, {
      onDelete: 'cascade',
    }),
    name: text('name').notNull(),
    format: text('format').notNull(),
    fields: jsonb('fields')
      .notNull()
      .default(sql`'[]'::jsonb`),
    grouping: text('grouping').notNull().default('BY_DATE'),
    dateFormat: text('date_format').notNull().default('YYYY-MM-DD'),
    volumeUnit: text('volume_unit').notNull().default('BBL'),
    customHeader: text('custom_header'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
    createdByUserId: uuid('created_by_user_id').references(() => users.id, {
      onDelete: 'set null',
    }),
    archivedAt: timestamp('archived_at', { withTimezone: true }),
    archivedByUserId: uuid('archived_by_user_id').references(() => users.id, {
      onDelete: 'set null',
    }),
  },
  (t) => [
    check('disposal_report_templates_name_check', sql`char_length(name) BETWEEN 1 AND 200`),
    check('disposal_report_templates_format_check', sql`format IN ('CSV','EXCEL','PDF')`),
    check(
      'disposal_report_templates_grouping_check',
      sql`grouping IN ('BY_OPERATOR','BY_DATE','BY_DRIVER','NONE')`,
    ),
    check('disposal_report_templates_volume_unit_check', sql`volume_unit IN ('BBL','GAL','BOTH')`),
    index('disposal_report_templates_company_idx')
      .on(t.disposalCompanyId)
      .where(sql`archived_at IS NULL`),
    index('disposal_report_templates_facility_idx')
      .on(t.facilityId)
      .where(sql`archived_at IS NULL AND facility_id IS NOT NULL`),
    pgPolicy('disposal_report_templates_tenant_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`disposal_company_id IN (
        SELECT dc.id FROM disposal_companies dc
        WHERE dc.tenant_id::text = current_setting('app.current_tenant_id', true)
      )`,
    }),
  ],
);

export type DisposalReportTemplate = typeof disposalReportTemplates.$inferSelect;
export type NewDisposalReportTemplate = typeof disposalReportTemplates.$inferInsert;
export const disposalReportTemplateSelectSchema = createSelectSchema(disposalReportTemplates);
export const disposalReportTemplateInsertSchema = createInsertSchema(disposalReportTemplates);
